import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../../App/Services/AppService';
import UserNameLookup from '../Models/Commands/Lookups/UserNameLookup';
import InviteUserCommand from '../Models/Commands/UserCommands/InviteUserCommand';
import UnlockUserCommand from '../Models/Commands/UserCommands/UnlockUserCommand';

export interface IUsersApiClient {

    /**
     * Invited users endpoint
     * @returns A list of invited users.
     */
    getInvitedUsers(): AxiosPromise<Array<Model.PlainObject<UserNameLookup>>>;

    /**
     * Locked out users endpoint
     * @returns A list of locked out users.
     */
    getLockedOutUsers(): AxiosPromise<Array<Model.PlainObject<UserNameLookup>>>;

    /**
     * User full names endpoint
     * @returns A list of full names of users.
     */
    getUserFullNames(): AxiosPromise<Array<Model.PlainObject<UserNameLookup>>>;

    /**
     * Invite user endpoint
     * @param command Invite user command housing all related data about the request
     * @returns Task of IActionResult
     */
    inviteUser(command: Model.PartialPlainObject<InviteUserCommand>): AxiosPromise<any>;

    /**
     * Invite user endpoint
     * @param command Invite user command housing all related data about the request
     * @returns Task of IActionResult
     */
    reinviteUser(command: Model.PartialPlainObject<InviteUserCommand>): AxiosPromise<any>;

    /**
     * Unlock a user endpoint
     * @param command The unlock user command
     * @returns Task of ActionResult
     */
    unlockUser(command: Model.PartialPlainObject<UnlockUserCommand>): AxiosPromise<Model.PlainTrackedObject<any>>;

    // Client only properties / methods
}

@injectable()
export default class UsersApiClient extends Data.ApiClientBase implements IUsersApiClient {

    constructor(public config = AppService.get(Types.Config)) {
        super(`${config.identityServer.url}/api/users`);
    }

    public getInvitedUsers(): AxiosPromise<Array<Model.PlainObject<UserNameLookup>>> {
        return this.axios.get(`${this.apiPath}/getInvitedUsers`);
    }

    public getLockedOutUsers(): AxiosPromise<Array<Model.PlainObject<UserNameLookup>>> {
        return this.axios.get(`${this.apiPath}/getLockedOutUsers`);
    }

    public getUserFullNames(): AxiosPromise<Array<Model.PlainObject<UserNameLookup>>> {
        return this.axios.get(`${this.apiPath}/getUserFullNames`);
    }

    public inviteUser(command: Model.PartialPlainObject<InviteUserCommand>): AxiosPromise<any> {
        return this.axios.post(`${this.apiPath}/inviteUser`, command);
    }

    public reinviteUser(command: Model.PartialPlainObject<InviteUserCommand>): AxiosPromise<any> {
        return this.axios.post(`${this.apiPath}/reinvite`, command);
    }

    public unlockUser(command: Model.PartialPlainObject<UnlockUserCommand>): AxiosPromise<Model.PlainTrackedObject<any>> {
        return this.axios.post(`${this.apiPath}/unlock`, command);
    }

    // Client only properties / methods
}